import React, { useEffect, useState, useRef } from "react";
import { MapContainer, TileLayer, LayersControl, GeoJSON, useMap } from "react-leaflet";
import { Box } from "@mui/material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import * as L from "leaflet";
import "leaflet/dist/leaflet.css";
import "./Projections.css";
import Information from "./Information";
import { MapHomeControl, MapLegendControl } from "../../../common/MapControls/MapControls";

// const GEOSERVER_WMS_URL = "https://drr-dss.rimes.int/geoserver/test_lsi/wms?";
const GEOSERVER_WMS_URL = "https://drr-dss.rimes.int/climate_data?service?";

const INITIAL_VIEW_STATE = {
  longitude: 125.7275, // Center of Timor-Leste
  latitude: -8.8742,   // Center of Timor-Leste
  zoom: 8,             // Good zoom for a country view
};

// Removed InfoButton component entirely as per request

// =========================================================================
// WmsLandslideLayer component - Reused from LandslideForecast.js
// =========================================================================
const WmsLandslideLayer = ({ selectedLayerName }) => {
  const map = useMap();
  const wmsLayerRef = useRef(null);

  useEffect(() => {
    if (!map || !selectedLayerName) {
      // Clean up if no layer is selected
      if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
        map.removeLayer(wmsLayerRef.current);
        wmsLayerRef.current = null;
      }
      return;
    }

    const layerString = `test_lsi:${selectedLayerName}.tif`;
    console.log("Adding WMS Layer with name:", layerString);
    // The wmsBaseUrl is now the protocol-relative URL from the constant
    const wmsBaseUrl = GEOSERVER_WMS_URL;

    // Remove existing layer before adding new one
    if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
      map.removeLayer(wmsLayerRef.current);
      wmsLayerRef.current = null;
    }

    const newWmsLayer = L.tileLayer.wms(wmsBaseUrl, {
      layers: layerString,
      format: "image/png",
      transparent: true,
      version: "1.1.0",
      crs: L.CRS.EPSG4326, // Or L.CRS.EPSG3857, choose based on Geoserver's optimal serving CRS
    });

    newWmsLayer.addTo(map);
    wmsLayerRef.current = newWmsLayer;

    // Cleanup function
    return () => {
      if (wmsLayerRef.current && map.hasLayer(wmsLayerRef.current)) {
        map.removeLayer(wmsLayerRef.current);
      }
    };
  }, [map, selectedLayerName]); // Re-run effect if map or selectedLayerName changes

  return null;
};
// =========================================================================
// End of WmsLandslideLayer component
// =========================================================================

// --- New component to handle map bounds imperatively ---
const MapBoundsUpdater = ({ bounds }) => {
  const map = useMap();

  useEffect(() => {
    if (bounds) {
      map.fitBounds(bounds);
    }
  }, [map, bounds]);

  return null;
};

// --- Simplified landslideLayers structure for a single dropdown ---
const landslideLayers = [
  { value: "proj_SSP1-26YR20-39", label: "2020 - 2039 (100-year RRP - SSP 1-2.6)" },
  { value: "proj_SSP1-26YR40-59", label: "2040 - 2059 (100-year RRP - SSP 1-2.6)" },
  { value: "proj_SSP1-26YR60-79", label: "2060 - 2079 (100-year RRP - SSP 1-2.6)" },
  { value: "proj_SSP3-70YR20-39", label: "2020 - 2039 (100-year RRP - SSP 3-7.0)" },
  { value: "proj_SSP3-70YR40-59", label: "2040 - 2059 (100-year RRP - SSP 3-7.0)" },
  { value: "proj_SSP3-70YR60-79", label: "2060 - 2079 (100-year RRP - SSP 3-7.0)" },
  { value: "proj_SSP5-85YR20-39", label: "2020 - 2039 (100-year RRP - SSP 5-8.5)" },
  { value: "proj_SSP5-85YR40-59", label: "2040 - 2059 (100-year RRP - SSP 5-8.5)" },
  { value: "proj_SSP5-85YR60-79", label: "2060 - 2079 (100-year RRP - SSP 5-8.5)" },
];

const Projections = () => {
  const [selectedLayerName, setSelectedLayerName] = useState(landslideLayers[0].value);
  const [loading, setLoading] = useState(false);
  const [municipalityBoundary, setMunicipalityBoundary] = useState(null);
  const [mapBounds, setMapBounds] = useState(null);

  // Load municipality boundary GeoJSON (if still needed)
  useEffect(() => {
    const loadMunicipalityBoundary = async () => {
      try {
        const response = await fetch("/DATA/OSMbased_adm3_boundary.json");
        const data = await response.json();
        setMunicipalityBoundary(data); 

        // ADD THIS NEW LOGIC
        if (data && data.features && data.features.length > 0) {
          const geoJsonLayer = L.geoJSON(data);
          setMapBounds(geoJsonLayer.getBounds());
        }

      } catch (error) {
        console.error("Error loading municipality boundary:", error);
      }
    };
    loadMunicipalityBoundary();
  }, []);

  // Single handler for the combined dropdown
  const handleLayerSelect = (event) => {
    setLoading(true);
    const newLayerValue = event.target.value;
    setSelectedLayerName(newLayerValue);
    setTimeout(() => setLoading(false), 700);
  };

  return (
    <div className="map-container-WindSpeedCyclone" style={{ backgroundColor: "#f2f3f4", height: "auto" }}>
      {/* Filter Bar - above map */}
      <Box
        sx={{
          p: 2.5,
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          boxShadow: "0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.03)",
          border: "1px solid #e8ecf1",
          marginBottom: "16px",
          overflow: "visible",
          position: "relative",
          zIndex: 1,
        }}
      >
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          {/* Filter Header */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
            <FilterAltIcon sx={{ color: "#0d6efd", fontSize: 22 }} />
            <span style={{ fontWeight: 700, fontSize: "15px", color: "#333" }}>Filter</span>
          </Box>

          {/* Divider */}
          <Box sx={{ width: "1px", height: "36px", backgroundColor: "#e0e0e0" }} />

          {/* Projection Dropdown */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <span style={{ fontWeight: 600, fontSize: "14px", color: "#333", whiteSpace: "nowrap" }}>Projection:</span>
            <select
              id="landslide-projection-select"
              className="dropdown"
              onChange={handleLayerSelect}
              value={selectedLayerName}
              style={{ fontSize: "13px", padding: "6px 10px", width: "320px" }}
            >
              {landslideLayers.map((layer) => (
                <option key={layer.value} value={layer.value}>
                  {layer.label}
                </option>
              ))}
            </select>
          </Box>
        </Box>
      </Box>

      <div className="map-wrapper">
        {loading && (
          <div className="map-loading-overlay">
            <div className="spinner"></div>
            <p>Loading map layers...</p>
          </div>
        )}

        <MapContainer
          center={[INITIAL_VIEW_STATE.latitude, INITIAL_VIEW_STATE.longitude]}
          zoom={INITIAL_VIEW_STATE.zoom}
          className="mapcontainerall"
          style={{ height: "calc(100vh - 270px)" }}
        >
          <MapHomeControl />
          <MapBoundsUpdater bounds={mapBounds} />

          <Information />

          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />

          {selectedLayerName && (
            <WmsLandslideLayer selectedLayerName={selectedLayerName} />
          )}

          <LayersControl position="topright">
            {/* Municipality boundary from local GeoJSON */}
            {municipalityBoundary && (
              <LayersControl.Overlay name="Suco" checked>
                <GeoJSON
                  data={municipalityBoundary}
                  style={{ color: "black", weight: 2, fillOpacity: 0 }}
                />
              </LayersControl.Overlay>
            )}
          </LayersControl>

          <MapLegendControl
            position="bottomright"
            sections={[
              {
                shape: "square",
                items: [
                  { label: "High", color: "rgb(230, 45, 58)" },
                  { label: "Medium", color: "rgb(243, 155, 58)" },
                  { label: "Low", color: "rgb(243, 235, 18)" },
                  { label: "No-Risk", color: "rgb(148, 203, 85)" },
                ],
              },
            ]}
          />

          {/* <div className="map-footer">
            <p>{selectedDescription}</p>
          </div> */}
        </MapContainer>
      </div>
    </div>
  );
};

export default Projections;